import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams, type RouteObject } from 'react-router-dom';

import { GUIDE_TYPE_SECTIONS } from './lib/guideRoutes';
import { AppLayout }      from './layouts/AppLayout';
import { DocsLayout }     from './layouts/DocsLayout';
import { SettingsLayout } from './layouts/SettingsLayout';
import { ErrorPage }      from './pages/ErrorPage';
import { useAuthStore }   from './stores/auth';
import { useProjectStore } from './stores/project';

// ── Lazy page imports ─────────────────────────────────────────────────────────
// Each page becomes its own chunk — downloaded once, cached forever.
const AccountSettingsPage     = lazy(() => import('./pages/AccountSettingsPage').then(m     => ({ default: m.AccountSettingsPage })));
const AgentDetailPage         = lazy(() => import('./pages/AgentDetailPage').then(m         => ({ default: m.AgentDetailPage })));
const AgentsPage              = lazy(() => import('./pages/AgentsPage').then(m              => ({ default: m.AgentsPage })));
const AnalyticsDashboardPage  = lazy(() => import('./pages/AnalyticsDashboardPage').then(m  => ({ default: m.AnalyticsDashboardPage })));
const AnalyticsPage           = lazy(() => import('./pages/AnalyticsPage').then(m           => ({ default: m.AnalyticsPage })));
const AppearancePage          = lazy(() => import('./pages/AppearancePage').then(m          => ({ default: m.AppearancePage })));
const BrandingPage            = lazy(() => import('./pages/BrandingPage').then(m            => ({ default: m.BrandingPage })));
const ChecklistPage           = lazy(() => import('./pages/ChecklistPage').then(m           => ({ default: m.ChecklistPage })));
const CollectionDetailPage    = lazy(() => import('./pages/CollectionDetailPage').then(m    => ({ default: m.CollectionDetailPage })));
const CollectionsPage         = lazy(() => import('./pages/CollectionsPage').then(m         => ({ default: m.CollectionsPage })));
const DashboardPage           = lazy(() => import('./pages/DashboardPage').then(m           => ({ default: m.DashboardPage })));
const DemoPage                = lazy(() => import('./pages/DemoPage').then(m                => ({ default: m.DemoPage })));
const DocsApiPage             = lazy(() => import('./pages/docs/DocsApiPage').then(m        => ({ default: m.DocsApiPage })));
const DocsChatPage            = lazy(() => import('./pages/docs/DocsChatPage').then(m       => ({ default: m.DocsChatPage })));
const DocsExtensionPage       = lazy(() => import('./pages/docs/DocsExtensionPage').then(m  => ({ default: m.DocsExtensionPage })));
const DocsOverviewPage        = lazy(() => import('./pages/docs/DocsOverviewPage').then(m   => ({ default: m.DocsOverviewPage })));
const DocsSdkPage             = lazy(() => import('./pages/docs/DocsSdkPage').then(m        => ({ default: m.DocsSdkPage })));
const DocsThemePage           = lazy(() => import('./pages/docs/DocsThemePage').then(m      => ({ default: m.DocsThemePage })));
const GuideDetailPage         = lazy(() => import('./pages/GuideDetailPage').then(m         => ({ default: m.GuideDetailPage })));
const GuideFlowPage           = lazy(() => import('./pages/GuideFlowPage').then(m           => ({ default: m.GuideFlowPage })));
const FlowsPage               = lazy(() => import('./pages/FlowsPage').then(m               => ({ default: m.FlowsPage })));
const GuidesPage              = lazy(() => import('./pages/GuidesPage').then(m              => ({ default: m.GuidesPage })));
const KnowledgeBaseDetailPage = lazy(() => import('./pages/KnowledgeBaseDetailPage').then(m => ({ default: m.KnowledgeBaseDetailPage })));
const KnowledgeBasesPage      = lazy(() => import('./pages/KnowledgeBasesPage').then(m     => ({ default: m.KnowledgeBasesPage })));
const LoginPage               = lazy(() => import('./pages/LoginPage').then(m               => ({ default: m.LoginPage })));
const OverviewPage            = lazy(() => import('./pages/OverviewPage').then(m            => ({ default: m.OverviewPage })));
const ProjectSettingsPage     = lazy(() => import('./pages/ProjectSettingsPage').then(m     => ({ default: m.ProjectSettingsPage })));
const ProjectsPage            = lazy(() => import('./pages/ProjectsPage').then(m            => ({ default: m.ProjectsPage })));
const PublicSharePage         = lazy(() => import('./pages/PublicSharePage').then(m         => ({ default: m.PublicSharePage })));
const PublicRecordingPage     = lazy(() => import('./pages/PublicRecordingPage').then(m     => ({ default: m.PublicRecordingPage })));
const MediaLibraryPage        = lazy(() => import('./pages/MediaLibraryPage').then(m        => ({ default: m.MediaLibraryPage })));
const RecordingDetailPage     = lazy(() => import('./pages/RecordingDetailPage').then(m     => ({ default: m.RecordingDetailPage })));
const RecordingsPage          = lazy(() => import('./pages/RecordingsPage').then(m          => ({ default: m.RecordingsPage })));
const TeamMembersPage         = lazy(() => import('./pages/TeamMembersPage').then(m         => ({ default: m.TeamMembersPage })));
const WorkflowViewPage        = lazy(() => import('./pages/workflow/WorkflowViewPage').then(m => ({ default: m.WorkflowViewPage })));
const WorkflowsPage           = lazy(() => import('./pages/WorkflowsPage').then(m           => ({ default: m.WorkflowsPage })));
const WorkflowOrganizerPage   = lazy(() => import('./pages/WorkflowOrganizerPage').then(m   => ({ default: m.WorkflowOrganizerPage })));
const AvatarListPage          = lazy(() => import('./pages/AvatarListPage').then(m           => ({ default: m.AvatarListPage })));
const AvatarDetailPage        = lazy(() => import('./pages/AvatarDetailPage').then(m         => ({ default: m.AvatarDetailPage })));
const AvatarPreviewPage       = lazy(() => import('./pages/AvatarPreviewPage').then(m        => ({ default: m.AvatarPreviewPage })));

// Shown while a page chunk downloads on first visit
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-32 opacity-30">
      <div className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
    </div>
  );
}

// Suspense boundary — P wraps every lazy route element
function P({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function LegacyProjectExportRedirect() {
  const { id } = useParams();
  return <Navigate to={`/projects/${id}/settings#exports`} replace />;
}

function LegacyPdfExportRedirect() {
  const projectId = useProjectStore((s) => s.activeProjectId);
  if (!projectId) return <Navigate to="/settings/projects" replace />;
  return <Navigate to={`/projects/${projectId}/settings#exports`} replace />;
}

function RequireAuth() {
  const token = useAuthStore((s) => s.token);
  const location = useLocation();
  if (!token) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <Outlet />;
}

/** Legacy /walkthroughs URLs → /workflows */
function LegacyWalkthroughRedirect({ suffix = '' }: { suffix?: string }) {
  const { id } = useParams();
  if (!id) return <Navigate to="/workflows" replace />;
  return <Navigate to={`/workflows/${id}${suffix}`} replace />;
}

const routes: RouteObject[] = [
  // Standalone full-screen routes (no sidebar / header)
  { path: '/login',                      element: <P><LoginPage /></P>,       errorElement: <ErrorPage /> },
  { path: '/share/:token',               element: <P><PublicSharePage /></P>, errorElement: <ErrorPage /> },
  { path: '/r/:slug',                    element: <P><PublicRecordingPage /></P>, errorElement: <ErrorPage /> },
  { path: '/workflows/:id/demo',         element: <P><DemoPage /></P>,        errorElement: <ErrorPage /> },
  { path: '/workflows/:id/checklist',    element: <P><ChecklistPage /></P>,   errorElement: <ErrorPage /> },
  { path: '/walkthroughs/:id/demo',      element: <LegacyWalkthroughRedirect suffix="/demo" />, errorElement: <ErrorPage /> },
  { path: '/walkthroughs/:id/checklist', element: <LegacyWalkthroughRedirect suffix="/checklist" />, errorElement: <ErrorPage /> },

  // Settings shell — standalone, not inside the app sidebar
  {
    path: '/settings',
    element: <SettingsLayout />,
    errorElement: <ErrorPage />,
    children: [
      { index: true,        element: <Navigate to="account" replace /> },
      { path: 'account',    element: <P><AccountSettingsPage /></P>,   errorElement: <ErrorPage /> },
      { path: 'team',       element: <P><TeamMembersPage /></P>,       errorElement: <ErrorPage /> },
      { path: 'branding',   element: <P><BrandingPage /></P>,          errorElement: <ErrorPage /> },
      { path: 'pdf-export', element: <LegacyPdfExportRedirect />, errorElement: <ErrorPage /> },
      { path: 'appearance', element: <P><AppearancePage /></P>,        errorElement: <ErrorPage /> },
      { path: 'projects',   element: <P><ProjectsPage /></P>,          errorElement: <ErrorPage /> },
    ],
  },

  // App shell
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: 'docs',
        element: <DocsLayout />,
        errorElement: <ErrorPage />,
        children: [
          { index: true,         element: <Navigate to="overview" replace /> },
          { path: 'overview',    element: <P><DocsOverviewPage /></P>,  errorElement: <ErrorPage /> },
          { path: 'sdk',         element: <P><DocsSdkPage /></P>,        errorElement: <ErrorPage /> },
          { path: 'theming',     element: <P><DocsThemePage /></P>,      errorElement: <ErrorPage /> },
          { path: 'api',         element: <P><DocsApiPage /></P>,        errorElement: <ErrorPage /> },
          { path: 'chat',        element: <P><DocsChatPage /></P>,       errorElement: <ErrorPage /> },
          { path: 'extension',   element: <P><DocsExtensionPage /></P>,  errorElement: <ErrorPage /> },
        ],
      },
      {
        element: <RequireAuth />,
        errorElement: <ErrorPage />,
        children: [
          { index: true,                              element: <P><DashboardPage /></P>,          errorElement: <ErrorPage /> },
          { path: 'workflows',                        element: <P><WorkflowsPage /></P>,          errorElement: <ErrorPage /> },
          { path: 'workflows/organize',               element: <P><WorkflowOrganizerPage /></P>,  errorElement: <ErrorPage /> },
          { path: 'workflows/:id',                    element: <P><WorkflowViewPage /></P>,       errorElement: <ErrorPage /> },
          { path: 'workflows/:id/analytics',          element: <P><AnalyticsPage /></P>,          errorElement: <ErrorPage /> },
          { path: 'walkthroughs',                     element: <Navigate to="/workflows" replace /> },
          { path: 'walkthroughs/:id',                 element: <LegacyWalkthroughRedirect /> },
          { path: 'walkthroughs/:id/analytics',       element: <LegacyWalkthroughRedirect suffix="/analytics" /> },
          { path: 'collections',                      element: <P><CollectionsPage /></P>,        errorElement: <ErrorPage /> },
          { path: 'collections/:id',                  element: <P><CollectionDetailPage /></P>,   errorElement: <ErrorPage /> },
          { path: 'guides',                           element: <Navigate to="/guide-blocks" replace /> },
          { path: 'guide-blocks',                     element: <P><GuidesPage fixedType="walkthrough" /></P>, errorElement: <ErrorPage /> },
          { path: 'hints',                            element: <P><GuidesPage fixedType="hint" /></P>, errorElement: <ErrorPage /> },
          { path: 'announcements',                    element: <P><GuidesPage fixedType="announcement" /></P>, errorElement: <ErrorPage /> },
          { path: 'checklists',                       element: <P><GuidesPage fixedType="checklist" /></P>, errorElement: <ErrorPage /> },
          { path: 'choices',                          element: <P><GuidesPage fixedType="choice" /></P>, errorElement: <ErrorPage /> },
          { path: 'surveys',                          element: <P><GuidesPage fixedType="survey" /></P>, errorElement: <ErrorPage /> },
          ...GUIDE_TYPE_SECTIONS.map((section) => ({
            path: `${section.listPath.replace(/^\//, '')}/:id`,
            element: <P><GuideDetailPage /></P>,
            errorElement: <ErrorPage />,
          })),
          { path: 'guides/:id',                       element: <P><GuideDetailPage /></P>,        errorElement: <ErrorPage /> },
          { path: 'flow',                             element: <P><FlowsPage /></P>,              errorElement: <ErrorPage /> },
          { path: 'flow/:id',                         element: <P><GuideFlowPage /></P>,          errorElement: <ErrorPage /> },
          { path: 'overview',                         element: <P><OverviewPage /></P>,           errorElement: <ErrorPage /> },
          { path: 'analytics',                        element: <P><AnalyticsDashboardPage /></P>, errorElement: <ErrorPage /> },
          { path: 'knowledge-bases',                  element: <P><KnowledgeBasesPage /></P>,     errorElement: <ErrorPage /> },
          { path: 'knowledge-bases/:id',              element: <P><KnowledgeBaseDetailPage /></P>, errorElement: <ErrorPage /> },
          { path: 'agents',                           element: <P><AgentsPage /></P>,             errorElement: <ErrorPage /> },
          { path: 'agents/:id',                       element: <P><AgentDetailPage /></P>,        errorElement: <ErrorPage /> },
          { path: 'recordings',                        element: <P><RecordingsPage /></P>,         errorElement: <ErrorPage /> },
          { path: 'recordings/:id',                    element: <P><RecordingDetailPage /></P>,    errorElement: <ErrorPage /> },
          { path: 'media',                             element: <P><MediaLibraryPage /></P>,       errorElement: <ErrorPage /> },
          { path: 'projects/:id/settings',            element: <P><ProjectSettingsPage /></P>,    errorElement: <ErrorPage /> },
          { path: 'projects/:id/export-layout',       element: <LegacyProjectExportRedirect />,   errorElement: <ErrorPage /> },
          { path: 'avatars',                          element: <P><AvatarListPage /></P>,         errorElement: <ErrorPage /> },
          { path: 'avatars/:id',                      element: <P><AvatarDetailPage /></P>,       errorElement: <ErrorPage /> },
          { path: 'avatars/:id/preview',              element: <P><AvatarPreviewPage /></P>,      errorElement: <ErrorPage /> },
        ],
      },

      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
